import { Box, Text } from 'ink';
import { useEffect, useState } from 'react';
import throttle from 'lodash.throttle';
import { Build } from '@todesktop/shared';
import BuildCompleteMessage from './BuildCompleteMessage';
import PlatformProgress from './PlatformProgress';
import IntrospectionNotice from './IntrospectionNotice';
import { hasBuildKickedOff } from '../utilities/buildStatus';
import logForCI from '../utilities/logForCI';
import useExit from '../utilities/useExit';

const logForCIThrottled = throttle(logForCI, 60 * 1000, { trailing: true });

export default function BuildProgress({
  build,
  onBuildFailure,
}: {
  build: Build;
  onBuildFailure: (message: string) => void;
}) {
  const exit = useExit();

  const [{ hasKickedOff }, setState] = useState({
    hasKickedOff: false,
  });
  const platformsNotSkipped = (['windows', 'mac', 'linux'] as const).filter(
    (platform) => !build[platform]!.shouldSkip,
  );

  useEffect(() => {
    setState((previousState) => ({
      ...previousState,
      hasKickedOff: hasBuildKickedOff(build),
    }));
  }, [build]);

  useEffect(() => {
    if (build.status === 'failed') {
      onBuildFailure(build.errorMessage!);
    } else if (build.status === 'cancelled') {
      setTimeout(exit, 10);
    } else {
      const ciPlatformProgress = platformsNotSkipped.map(
        (platform) =>
          `${platform.charAt(0).toUpperCase() + platform.slice(1)}: ${
            build[platform]!.progressActivityName
          } (${build[platform]!.progressPercentage}%)`,
      );
      logForCIThrottled(ciPlatformProgress.join(', '));
    }
  }, [build, exit, onBuildFailure, platformsNotSkipped]);

  if (build.status === 'succeeded') {
    return <BuildCompleteMessage build={build} />;
  }

  if (build.status === 'cancelled') {
    return (
      <Box flexDirection="column" marginBottom={1}>
        <Text>Build cancelled</Text>
      </Box>
    );
  }

  if (!hasKickedOff) {
    return null;
  }

  return (
    <>
      <IntrospectionNotice build={build} />
      <Box flexDirection="column">
        {platformsNotSkipped.map((platform) => (
          <Box flexDirection="column" key={platform}>
            <PlatformProgress
              activityName={
                build[platform]!.status === 'failed'
                  ? build[platform]!.errorMessage!
                  : build[platform]!.progressActivityName!
              }
              activityType={
                build[platform]!.status === 'failed'
                  ? 'error'
                  : build[platform]!.progressActivityType!
              }
              downloadUrl={build[platform]!.standardDownloadUrl}
              percent={build[platform]!.progressPercentage!}
              platform={platform}
              shouldShowReadyPrefix={
                !(build && ['failed', 'succeeded'].includes(build.status))
              }
            />
          </Box>
        ))}
      </Box>
    </>
  );
}
